import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Check, CreditCard, Loader2, LogOut, ShieldCheck, Sparkles, XCircle, RotateCcw, ShieldOff } from "lucide-react";
import { trackBeginCheckout } from "@/lib/analytics";

const FEATURES = [
  "Editor visuale campi PDF illimitato",
  "Batch identico su cartella intera",
  "Mail-merge da CSV con placeholder {colonna}",
  "Compilazione AcroForm nativi",
  "Salvataggio diretto nella cartella locale",
  "Template salvati per riuso futuro",
];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function SubscribePage() {
  const { user, logout, refresh } = useAuth();
  const { locale } = useLocale();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const alreadyTrialed = !!user?.trial_end;
  const status = user?.subscription_status;
  const cancelAtPeriodEnd = !!user?.cancel_at_period_end;
  // Stripe may set status="canceled" directly (e.g. when the user cancels
  // through the hosted Customer Portal instead of our in-app button which
  // only flips cancel_at_period_end).
  const stripeCanceled = status === "canceled";
  const wasCanceled = cancelAtPeriodEnd || stripeCanceled;

  // `has_access` is the authoritative source of truth computed by the backend:
  // super-admin OR has_paid OR trial_end>now OR (period_end>now && status active/trialing).
  const hasAccess = !!user?.has_access;

  // Access end date = the future date up to which the user can still use the app.
  // Prefer trial_end while trial is running, otherwise current_period_end.
  const nowMs = Date.now();
  const trialEndMs = user?.trial_end ? new Date(user.trial_end).getTime() : null;
  const periodEndMs = user?.current_period_end ? new Date(user.current_period_end).getTime() : null;
  const trialStillValid = !!(trialEndMs && trialEndMs > nowMs);
  const accessEndIso =
    trialStillValid ? user?.trial_end : (periodEndMs && periodEndMs > nowMs ? user?.current_period_end : null);

  // Effective states (mutually exclusive, computed in order):
  //   1. isAdminDisabled: super-admin flipped payment off manually
  //   2. isCanceledButValid: canceled but access still valid until accessEndIso
  //   3. isTrialActive: currently in a running trial (not canceled)
  //   4. isSubscriptionActive: paid subscription running (not canceled)
  //   5. isPastDue: payment failure, retry attempts running
  //   6. else: truly expired -> show pricing
  const isAdminDisabled =
    user?.payment_source === "manual" && !hasAccess && status !== "trialing" && status !== "active";
  const isCanceledButValid = !isAdminDisabled && hasAccess && wasCanceled && !!accessEndIso;
  // Free trial lato backend (nuovo flusso Feb 2026): hasAccess && !has_paid &&
  // trial_end>now, senza stripe subscription attiva.
  const isFreeTrialActive = !isAdminDisabled && !isCanceledButValid && hasAccess && !user?.has_paid && trialStillValid && !status;
  const isStripeTrialActive = !isAdminDisabled && !isCanceledButValid && hasAccess && status === "trialing";
  const isTrialActive = isFreeTrialActive || isStripeTrialActive;
  const isSubscriptionActive = !isAdminDisabled && !isCanceledButValid && hasAccess && status === "active";
  const isPastDue = !isAdminDisabled && !isCanceledButValid && hasAccess && status === "past_due";
  const showActiveBlock = isTrialActive || isSubscriptionActive || isPastDue;
  const showPricing = !isAdminDisabled && !isCanceledButValid && !showActiveBlock;

  // Resume is only technically supported by /stripe/resume-subscription when
  // cancel_at_period_end=true (subscription still exists on Stripe). If Stripe
  // hard-canceled the sub, the user must start a fresh checkout.
  const canResumeInPlace = cancelAtPeriodEnd && !stripeCanceled;

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stripe/create-checkout-session", {
        origin_url: window.location.origin,
        locale,
      });
      if (data?.checkout_url) {
        // GA4 begin_checkout window: guarantee AT LEAST 7000ms total between
        // gaStartedAt and the redirect. The 7000ms are COMPLESSIVI (not
        // additive to the internal 800ms cap of trackBeginCheckout): whatever
        // time is spent inside trackBeginCheckout() counts against the 7000ms
        // budget. If GA4 is blocked/slow and the call times out, we STILL
        // redirect afterwards.
        const gaStartedAt = Date.now();
        toast.success("Reindirizzo su Stripe…");
        const gaResult = await trackBeginCheckout();
        console.info("[GA4] begin_checkout result", gaResult);
        const gaElapsed = Date.now() - gaStartedAt;
        const gaRemaining = Math.max(0, 7000 - gaElapsed);
        if (gaRemaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, gaRemaining));
        }
        window.location.href = data.checkout_url;
        return;
      }
      throw new Error("URL di checkout mancante");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Cancellare l'abbonamento? Manterrai l'accesso fino alla fine del periodo pagato.")) return;
    setCancelBusy(true);
    try {
      const { data } = await api.post("/stripe/cancel-subscription");
      if (refresh) refresh(data);
      toast.success("Abbonamento cancellato. Accesso attivo fino a fine periodo.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCancelBusy(false);
    }
  };

  const resumeSubscription = async () => {
    setCancelBusy(true);
    try {
      const { data } = await api.post("/stripe/resume-subscription");
      if (refresh) refresh(data);
      toast.success("Abbonamento riattivato.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#050B1F] text-white font-geist">
      <header className="border-b border-white/[0.08] bg-[#050B1F]/95 backdrop-blur-sm relative">
        <div className="absolute inset-0 auth-grid pointer-events-none opacity-40" />
        <div className="relative flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-4">
            <div className="h-8 w-8 rounded-sm bg-gradient-to-br from-[#4D8AFF] to-[#002FA7] flex items-center justify-center shadow-[0_0_20px_-4px_rgba(77,138,255,0.55)]">
              <span className="font-geist-mono text-[13px] font-bold text-white">R</span>
            </div>
            <div className="flex flex-col leading-tight">
              <div className="font-geist text-[15px] font-semibold text-white tracking-tight">
                Revo <span className="text-[#4D8AFF]">PDF</span> Loader
              </div>
              <div className="font-geist-mono text-[9px] tracking-[0.2em] uppercase text-white/40">
                by FudoSoftware
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.has_access && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="subscribe-back-btn"
                onClick={() => nav("/")}
                className="rounded-sm hover:bg-white/[0.06] hover:text-white text-white/70 font-geist-mono text-[11px] uppercase tracking-wider"
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Editor
              </Button>
            )}
            <span className="font-geist-mono text-[11px] text-white/50 hidden sm:inline" data-testid="user-email">
              {user?.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="subscribe-logout-btn"
              onClick={logout}
              className="rounded-sm hover:bg-white/[0.06] hover:text-white text-white/70 font-geist-mono text-[11px] uppercase tracking-wider"
            >
              <LogOut className="h-3.5 w-3.5 mr-1" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-14 relative">
        <div className="absolute inset-0 auth-grid pointer-events-none opacity-30" />
        <div className="relative w-full max-w-3xl" data-testid="subscribe-page">
          {isAdminDisabled ? (
            <div
              className="rounded-md border border-red-500/30 bg-red-500/[0.06] p-8 md:p-10"
              data-testid="subscribe-disabled-block"
            >
              <div className="h-14 w-14 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-6">
                <ShieldOff className="h-7 w-7 text-red-300" strokeWidth={2} />
              </div>
              <div className="font-geist-mono text-[10px] uppercase tracking-widest text-red-300 mb-3">
                Accesso sospeso
              </div>
              <h1
                className="font-geist text-2xl md:text-3xl font-semibold tracking-tight text-white leading-tight"
                data-testid="subscribe-disabled-title"
              >
                Il tuo abbonamento è stato disattivato.
              </h1>
              <p
                className="mt-4 font-geist text-[15px] text-white/70 leading-relaxed"
                data-testid="subscribe-disabled-body"
              >
                Per assistenza contatta{" "}
                <a
                  href="mailto:fudosoftware@gmail.com"
                  className="text-[#4D8AFF] hover:text-[#75A6FF] underline underline-offset-4 decoration-[#4D8AFF]/40"
                  data-testid="subscribe-disabled-mailto"
                >
                  fudosoftware@gmail.com
                </a>
                .
              </p>
              <div className="mt-8 pt-6 border-t border-white/[0.08] font-geist-mono text-[10px] uppercase tracking-widest text-white/40">
                Non puoi utilizzare l'app finché l'abbonamento non viene riattivato.
              </div>
            </div>
          ) : isCanceledButValid ? (
            <div data-testid="subscribe-canceled-valid-block">
              <div className="flex items-center gap-2 font-geist-mono text-[10px] tracking-widest uppercase text-amber-300 mb-4">
                <XCircle className="h-3 w-3" /> Abbonamento annullato
              </div>
              <h1
                className="font-geist text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight"
                data-testid="subscribe-canceled-title"
              >
                Il tuo abbonamento non verrà rinnovato.
              </h1>
              <p
                className="mt-4 font-geist text-[15px] text-white/75 leading-relaxed"
                data-testid="subscribe-canceled-body"
              >
                Puoi continuare a utilizzare REVO PDF Loader fino al{" "}
                <span className="text-white font-semibold" data-testid="access-end-date">
                  {formatDate(accessEndIso)}
                </span>
                . Dopo questa data l'accesso alle funzionalità incluse nell'abbonamento verrà disattivato.
              </p>
              <div className="mt-8 rounded-md border border-white/[0.08] bg-[#0A1738]/40 p-6">
                <div className="grid grid-cols-2 gap-4 font-geist-mono text-[11px]">
                  <div>
                    <div className="text-white/40 uppercase tracking-widest text-[10px]">Piano</div>
                    <div className="mt-1 text-white">Mensile · €9,90</div>
                  </div>
                  <div>
                    <div className="text-white/40 uppercase tracking-widest text-[10px]">Stato</div>
                    <div className="mt-1 text-amber-300" data-testid="subscription-status-value">
                      Annullato · accesso fino a fine periodo
                    </div>
                  </div>
                  <div>
                    <div className="text-white/40 uppercase tracking-widest text-[10px]">Accesso fino a</div>
                    <div className="mt-1 text-white">{formatDate(accessEndIso)}</div>
                  </div>
                </div>
                {canResumeInPlace ? (
                  <div className="mt-6 pt-6 border-t border-white/[0.08] flex flex-wrap gap-3">
                    <Button
                      data-testid="subscribe-resume-btn"
                      onClick={resumeSubscription}
                      disabled={cancelBusy}
                      className="rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-geist font-semibold h-10"
                    >
                      {cancelBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
                      Riattiva abbonamento
                    </Button>
                  </div>
                ) : (
                  <div className="mt-6 pt-6 border-t border-white/[0.08] flex flex-wrap gap-3">
                    <Button
                      data-testid="subscribe-resubscribe-btn"
                      onClick={startCheckout}
                      disabled={busy}
                      className="rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-geist font-semibold h-10"
                    >
                      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CreditCard className="h-3.5 w-3.5 mr-1" />}
                      Riattiva con un nuovo abbonamento
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : showActiveBlock ? (
            <>
              <div className="flex items-center gap-2 font-geist-mono text-[10px] tracking-widest uppercase text-[#4D8AFF] mb-4">
                <ShieldCheck className="h-3 w-3" /> Il tuo abbonamento
              </div>
              <h1
                className="font-geist text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight"
                data-testid="subscribe-active-title"
              >
                {isTrialActive && "Prova gratuita attiva"}
                {isSubscriptionActive && "Abbonamento attivo"}
                {isPastDue && "Pagamento in sospeso"}
              </h1>
              {isTrialActive && (
                <p className="mt-4 font-geist text-[15px] text-white/75 leading-relaxed" data-testid="subscribe-trial-body">
                  Puoi utilizzare tutte le funzionalità di REVO PDF Loader fino al{" "}
                  <span className="text-white font-semibold">{formatDate(user?.trial_end)}</span>.
                  {isFreeTrialActive
                    ? " La prova è gratuita, senza carta e senza addebiti automatici. Al termine, per continuare a usare le funzioni PDF dovrai abbonarti."
                    : " Successivamente partirà l'abbonamento mensile."}
                </p>
              )}
              <div className="mt-8 rounded-md border border-white/[0.08] bg-[#0A1738]/40 p-6">
                <div className="grid grid-cols-2 gap-4 font-geist-mono text-[11px]">
                  <div>
                    <div className="text-white/40 uppercase tracking-widest text-[10px]">Piano</div>
                    <div className="mt-1 text-white">Mensile · €9,90</div>
                  </div>
                  <div>
                    <div className="text-white/40 uppercase tracking-widest text-[10px]">Stato</div>
                    <div className="mt-1 text-white" data-testid="subscription-status-value">
                      {isTrialActive && "In prova"}
                      {isSubscriptionActive && "Attivo"}
                      {isPastDue && "In ritardo"}
                    </div>
                  </div>
                  {isTrialActive && (
                    <div>
                      <div className="text-white/40 uppercase tracking-widest text-[10px]">Fine prova</div>
                      <div className="mt-1 text-white">{formatDate(user?.trial_end)}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-white/40 uppercase tracking-widest text-[10px]">Prossimo rinnovo</div>
                    <div className="mt-1 text-white">{formatDate(user?.current_period_end)}</div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-white/[0.08] flex flex-wrap gap-3">
                  {isFreeTrialActive && (
                    <Button
                      data-testid="subscribe-upgrade-btn"
                      onClick={startCheckout}
                      disabled={busy}
                      className="rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-geist font-semibold h-10"
                    >
                      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CreditCard className="h-3.5 w-3.5 mr-1" />}
                      Abbonati a 9,90 € / mese
                    </Button>
                  )}
                  <Button
                    data-testid="subscribe-cancel-btn"
                    onClick={cancelSubscription}
                    disabled={cancelBusy || isFreeTrialActive}
                    variant="ghost"
                    className="rounded-sm text-red-300 hover:bg-red-500/10 hover:text-red-200 font-geist-mono text-[11px] uppercase tracking-wider"
                    style={isFreeTrialActive ? { display: "none" } : undefined}
                  >
                    {cancelBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
                    Cancella abbonamento
                  </Button>
                </div>
                {isFreeTrialActive && (
                  <p className="mt-3 font-geist-mono text-[10px] text-white/40 leading-relaxed">
                    Primo addebito oggi, poi €9,90 al mese. Puoi cancellare quando vuoi.
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 font-geist-mono text-[10px] tracking-widest uppercase text-[#4D8AFF] mb-4">
                <ShieldCheck className="h-3 w-3" /> Sblocca Revo PDF Loader
              </div>
              <h1
                className="font-geist text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight"
                data-testid="subscribe-expired-title"
              >
                {alreadyTrialed
                  ? "Il periodo di prova gratuito è terminato."
                  : "La tua prova gratuita di 7 giorni è terminata."}
                <span className="block text-white/50 font-geist text-lg mt-2">
                  Abbonati a €9,90/mese per riprendere subito. Primo addebito oggi, cancella quando vuoi.
                </span>
              </h1>

              <div className="mt-8 grid md:grid-cols-[minmax(0,1fr)_320px] gap-6">
                <div className="rounded-md border border-white/[0.08] bg-[#0A1738]/40 p-6">
                  <div className="font-geist-mono text-[10px] uppercase tracking-widest text-white/40 mb-4">
                    Cosa include
                  </div>
                  <ul className="space-y-3" data-testid="subscribe-features-list">
                    {FEATURES.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <span className="mt-0.5 h-5 w-5 rounded-sm bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
                          <Check className="h-3 w-3 text-emerald-300" strokeWidth={2.5} />
                        </span>
                        <span className="text-sm text-white/85 font-geist">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-white/[0.08] flex items-center gap-2 font-geist-mono text-[10px] uppercase tracking-widest text-white/40">
                    <Sparkles className="h-3 w-3 text-[#4D8AFF]" />
                    Carte di credito · PayPal · Google Pay · Apple Pay
                  </div>
                </div>

                <div className="rounded-md border border-[#4D8AFF]/25 bg-gradient-to-br from-[#0A1738]/60 to-[#050B1F] p-6 flex flex-col">
                  <div className="font-geist-mono text-[10px] uppercase tracking-widest text-[#4D8AFF] mb-3">
                    Abbonamento mensile
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-geist text-4xl font-semibold text-white">€9,90</span>
                    <span className="font-geist-mono text-[11px] text-white/50">/ mese</span>
                  </div>
                  {!alreadyTrialed && (
                    <div className="mt-2 inline-flex self-start px-2 py-0.5 rounded-sm bg-emerald-400/15 border border-emerald-400/30 font-geist-mono text-[10px] uppercase tracking-wider text-emerald-300">
                      Senza carta e senza addebiti automatici
                    </div>
                  )}
                  <div className="mt-4 space-y-2 font-geist-mono text-[10px] text-white/50">
                    <div>· Primo addebito oggi, poi €9,90/mese</div>
                    <div>· Cancella con un click, accesso fino a fine periodo</div>
                    <div>· Pagamento sicuro tramite Stripe</div>
                  </div>
                  <Button
                    data-testid="subscribe-start-btn"
                    onClick={startCheckout}
                    disabled={busy}
                    className="mt-6 w-full h-11 rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-geist font-semibold tracking-tight transition-colors"
                  >
                    {busy ? (
                      <span className="flex items-center gap-2 font-geist-mono text-xs uppercase tracking-wider">
                        <Loader2 className="h-4 w-4 animate-spin" /> Attendere…
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Abbonati a 9,90 € / mese
                      </span>
                    )}
                  </Button>
                  <div className="mt-3 text-center font-geist-mono text-[9px] uppercase tracking-widest text-white/30">
                    Puoi cancellare in qualsiasi momento
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
